import React from 'react'
import { defineMessages } from 'react-intl'

const LOCALES_WITH_CURRENCY_AFTER_AMOUNT = ['da', 'de', 'el', 'es', 'fr', 'nl-be', 'sv']

const locale = defineMessages({
  free: {
    id: 'event-carousel.free',
    defaultMessage: 'Free',
  },
  dateTimeRange: {
    id: 'event-carousel.date-time-range',
    defaultMessage: '{dateRange} at {timeRange}',
  },
  dateTime: {
    id: 'event-carousel.date-time',
    defaultMessage: '{date} at {time}',
  },
  plusMore: {
    id: 'event-carousel.plus-more',
    defaultMessage: ' + {count} more',
  },
})

const formattedDateRange = ({ start, end, intl, tz, showYear }) => {
  let result =
    intl.formatDate(start, { weekday: 'short', month: 'short', day: 'numeric', year: showYear && 'numeric' })

  if ((start.getDate() !== end.getDate()) || (start.getMonth() !== end.getMonth())) {
    result =
      `${result} - ${intl.formatDate(end, {
        weekday: 'short', month: 'short', day: 'numeric', year: showYear && 'numeric',
      })}`
  }

  if (tz) result = `${result} ${tz}`
  return result
}

const formattedTimeRange = ({ start, end, intl, tz }) => {
  let startTime = intl.formatTime(start).replace(':00', '')
  const period = startTime.split(' ')[1] // AM or PM
  const endTime = intl.formatTime(end).replace(':00', '')

  if (period && period === endTime.split(' ')[1]) {
    startTime = startTime.replace(` ${period}`, '')
  }

  let result = `${startTime}-${endTime}`
  if (tz) result = `${result} ${tz}`
  return result
}

const longFormattedTimeRange = ({ start, end, intl, tz }) => {
  let startTime = intl.formatTime(start)
  const period = startTime.split(' ')[1] // AM or PM
  const endTime = intl.formatTime(end)

  if (period && period === endTime.split(' ')[1]) {
    startTime = startTime.replace(` ${period}`, '')
  }

  let result = `${startTime}-${endTime}`
  if (tz) result = `${result} ${tz}`
  return result
}

const formatTimezoneOffset = (timeStamp) => {
  const offsetMinutes = new Date(timeStamp).getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '-' : '+'
  const absMinutes = Math.abs(offsetMinutes)
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0')
  const minutes = String(absMinutes % 60).padStart(2, '0')
  return `${sign}${hours}:${minutes}`
}

const dateWithTimezoneOffset = timeStamp => new Date(`${timeStamp}.000${formatTimezoneOffset(timeStamp)}`)

export const formattedDateFullTimeRange = ({
  startAt,
  endAt,
  intl,
  totalCount,
  tz,
  showYear,
}) => {
  let start
  let end
  if (typeof startAt === 'string') {
    start = dateWithTimezoneOffset(startAt)
    end = dateWithTimezoneOffset(endAt)
  } else {
    start = startAt
    end = endAt
  }

  const dateRange = formattedDateRange({ start, end, intl, showYear })
  const timeRange = longFormattedTimeRange({ start, end, intl })
  let result = intl.formatMessage(locale.dateTimeRange, { dateRange, timeRange })
  if (tz) result = `${result} ${tz}`

  return totalCount === 1 ?
    result :
    <span>{result}&nbsp;{intl.formatMessage(locale.plusMore, { count: totalCount - 1 })}</span>
}

export const formattedDateTimeRange = ({
  startAt,
  endAt,
  intl,
  totalCount,
  tz,
}) => {
  let start
  let end
  if (typeof startAt === 'string') {
    start = dateWithTimezoneOffset(startAt)
    end = dateWithTimezoneOffset(endAt)
  } else {
    start = startAt
    end = endAt
  }

  const dateRange = formattedDateRange({ start, end, intl, showYear: true })
  const timeRange = formattedTimeRange({ start, end, intl })
  let result = intl.formatMessage(locale.dateTimeRange, { dateRange, timeRange })
  if (tz) result = `${result} ${tz}`


  return totalCount === 1 ?
    result :
    <span>{result}&nbsp;{intl.formatMessage(locale.plusMore, { count: totalCount - 1 })}</span>
}

export const formattedMultipleDateRange = ({
  startAt,
  endAt,
  intl,
  totalCount,
  tz,
  showYear,
}) => {
  let start
  let end
  if (typeof startAt === 'string') {
    start = dateWithTimezoneOffset(startAt)
    end = dateWithTimezoneOffset(endAt)
  } else {
    start = startAt
    end = endAt
  }

  let result = formattedDateRange({ start, end, intl, showYear })
  if (tz) result = `${result} ${tz}`

  return totalCount === 1 ?
    result :
    <span>{result}&nbsp;{intl.formatMessage(locale.plusMore, { count: totalCount - 1 })}</span>
}

export const formattedTimeOnlyRange = ({ startAt, endAt, intl, tz }) => {
  let start
  let end
  if (typeof startAt === 'string') {
    start = dateWithTimezoneOffset(startAt)
    end = dateWithTimezoneOffset(endAt)
  } else {
    start = startAt
    end = endAt
  }

  return longFormattedTimeRange({ start, end, intl, tz })
}

export const formattedPriceRange = ({
  minPrice,
  maxPrice,
  currency,
  intl,
}) => {
  if (minPrice === null) return ''

  const formattedMinPrice = minPrice ?
    intl.formatNumber(minPrice, { style: 'currency', currency })
    :
    intl.formatMessage(locale.free)
  const formattedMaxPrice = maxPrice ?
    intl.formatNumber(maxPrice, { style: 'currency', currency })
    :
    intl.formatMessage(locale.free)

  // CA$10.00 or Free
  if (minPrice === maxPrice || !maxPrice) {
    return formattedMinPrice
  }

  // 10.00-20.00CA$ or Free-20.00CA$
  if (LOCALES_WITH_CURRENCY_AFTER_AMOUNT.includes(intl.locale)) {
    if (minPrice) {
      const minPriceWithoutCurrency = intl.formatNumber(
        minPrice,
        { minimumFractionDigits: 0, maximumFractionDigits: 2 },
      )
      return `${minPriceWithoutCurrency}-${formattedMaxPrice}`
    }
    return `${formattedMinPrice}-${formattedMaxPrice}`
  }

  // CA$10.00-20.00
  if (minPrice) {
    if (maxPrice) {
      const maxPriceWithoutCurrency = intl.formatNumber(
        maxPrice,
        { minimumFractionDigits: 0, maximumFractionDigits: 2 },
      )
      return `${formattedMinPrice}-${maxPriceWithoutCurrency}`
    }
    return `${formattedMinPrice}-${formattedMaxPrice}`
  }

  return `${formattedMinPrice}-${formattedMaxPrice}`
}

// value should show two decimals in all instances, except when .00
export const formatDecimalPlaces = (price) => {
  if (price) {
    const priceNum = parseFloat(price)
    return Number.isInteger(priceNum) ? priceNum : priceNum.toFixed(2)
  }
  return price
}


export const formattedPriceRangeEDP = ({
  minPrice,
  maxPrice,
  currency,
  intl,
}) => {
  if (minPrice === null) return ['']

  const formattedMinPrice = minPrice ?
    intl.formatNumber(formatDecimalPlaces(minPrice), { style: 'currency', currency })
    :
    intl.formatMessage(locale.free)
  const formattedMaxPrice = maxPrice ?
    intl.formatNumber(formatDecimalPlaces(maxPrice), { style: 'currency', currency })
    :
    intl.formatMessage(locale.free)

  // CA$10.00 or Free
  if (minPrice === maxPrice || !maxPrice) {
    return [formattedMinPrice]
  }

  // 10.00-20.00CA$ or Free-20.00CA$
  if (LOCALES_WITH_CURRENCY_AFTER_AMOUNT.includes(intl.locale)) {
    const absPrice = formattedMinPrice.match(/(?:[\s,.]?\d+)+/)
    return [`${minPrice && absPrice ? absPrice[0] : formattedMinPrice}`, `${formattedMaxPrice}`]
  }

  // CA$10.00-20.00
  return [formattedMinPrice, formattedMaxPrice]
}

export const formattedDateStartTime = ({
  startAt,
  intl,
  tz,
}) => {
  let start
  if (typeof startAt === 'string') {
    start = dateWithTimezoneOffset(startAt)
  } else {
    start = startAt
  }

  const date = formattedDateRange({ start, end: start, intl, showYear: true })
  const time = intl.formatTime(start)
  let result = intl.formatMessage(locale.dateTime, { date, time })
  if (tz) result = `${result} ${tz}`

  return result
}

export const formattedTime = (date, intl, tz) => {
  let dateTime
  if (typeof date === 'string') {
    dateTime = dateWithTimezoneOffset(date)
  } else {
    dateTime = date
  }
  let result = intl.formatTime(dateTime)
  if (tz) result = `${result} ${tz}`

  return result
}
